import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Alert, AlertDescription } from "./components/ui/alert";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { 
  Upload, 
  FileText, 
  Settings, 
  LogOut, 
  Plus, 
  Edit2, 
  Trash2, 
  AlertTriangle,
  Globe,
  CheckCircle,
  XCircle,
  Search,
  X,
  ArrowUpDown,
  Building2
} from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://bank-reconcile-4.preview.emergentagent.com';
const API = `${BACKEND_URL}/api`;

// Translations
const translations = {
  es: {
    // Login
    login: "Iniciar Sesión",
    username: "Usuario",
    password: "Contraseña",
    loginButton: "Entrar",
    invalidCredentials: "Credenciales inválidas",
    
    // Navigation
    uploadData: "Cargar Datos",
    referenceCodes: "Códigos de Referencia",
    bankManagement: "Bancos",
    processedData: "Datos Procesados",
    settings: "Configuración",
    logout: "Cerrar Sesión",
    
    // Upload
    uploadCSV: "Cargar Archivo CSV",
    dragDropFile: "Arrastra tu archivo CSV aquí, o haz clic para seleccionar",
    uploadFile: "Subir Archivo",
    processing: "Procesando...",
    uploadSuccess: "Archivo subido exitosamente",
    uploadError: "Error al subir archivo",
    newCodesFound: "Nuevos códigos encontrados",
    addNewCodes: "Agregar nuevos códigos",
    bankName: "Nombre del Banco",
    bankPlaceholder: "Escriba el nombre del banco (ej: Banco Santander, BBVA, etc.)",
    selectBank: "Seleccionar un banco",
    noBanksFound: "No hay bancos configurados",
    addBanksFirst: "Primero debe agregar bancos en la sección 'Bancos'",
    
    // Removed mapping translations - will be replaced with new logic
    
    // Missing Reference Codes Dialog
    missingReferenceCodes: "Códigos de Referencia Faltantes",
    missingCodesDescription: "El archivo CSV no se puede procesar porque contiene códigos de referencia que no existen en el sistema.",
    missingCodesList: "Códigos faltantes:",
    addMissingCodes: "Agregar Códigos Faltantes",
    goToReferenceCodes: "Ir a Códigos de Referencia",
    closeMissingCodes: "Cerrar",
    
    // Reference Codes
    manageReferenceCodes: "Gestionar Códigos de Referencia",
    addCode: "Agregar Código",
    operativeCode: "Código Operativo",
    description: "Descripción",
    actions: "Acciones",
    edit: "Editar",
    delete: "Eliminar",
    save: "Guardar",
    cancel: "Cancelar",
    codeExists: "El código ya existe",
    
    // Processed Data
    bankReconciliation: "Conciliación Bancaria",
    order: "Orden",
    processed: "Procesado",
    date: "Fecha",
    type: "Tipo",
    concept: "Concepto",
    debits: "Débitos",
    credits: "Créditos",
    balance: "Saldo",
    totalDebits: "Total Débitos",
    totalCredits: "Total Créditos",
    
    // Settings
    languageSettings: "Configuración de Idioma",
    selectLanguage: "Seleccionar Idioma",
    spanish: "Español",
    english: "English",
    profileSettings: "Configuración de Perfil",
    changePassword: "Cambiar Contraseña",
    changeEmail: "Cambiar Email",
    currentPassword: "Contraseña Actual",
    newPassword: "Nueva Contraseña",
    confirmPassword: "Confirmar Contraseña",
    newEmail: "Nuevo Email",
    resetDatabase: "Reiniciar Base de Datos",
    dangerZone: "Zona de Peligro",
    resetDatabaseWarning: "¡ADVERTENCIA! Esta acción eliminará todos los datos de movimientos bancarios y códigos de referencia. Esta acción no se puede deshacer.",
    confirmReset: "Confirmar Reinicio",
    enterPasswordToConfirm: "Ingresa tu contraseña para confirmar",
    
    // General
    loading: "Cargando...",
    noData: "No hay datos disponibles",
    error: "Error",
    success: "Éxito"
  },
  en: {
    // Login
    login: "Login",
    username: "Username",
    password: "Password",
    loginButton: "Sign In",
    invalidCredentials: "Invalid credentials",
    
    // Navigation
    uploadData: "Upload Data",
    referenceCodes: "Reference Codes",
    bankManagement: "Banks",
    processedData: "Processed Data",
    settings: "Settings",
    logout: "Logout",
    
    // Upload
    uploadCSV: "Upload CSV File",
    dragDropFile: "Drag your CSV file here, or click to select",
    uploadFile: "Upload File",
    processing: "Processing...",
    uploadSuccess: "File uploaded successfully",
    uploadError: "Error uploading file",
    newCodesFound: "New codes found",
    addNewCodes: "Add new codes",
    bankName: "Bank Name",
    bankPlaceholder: "Enter the bank name (e.g: Banco Santander, BBVA, etc.)",
    selectBank: "Select a bank",
    noBanksFound: "No banks configured",
    addBanksFirst: "You must first add banks in the 'Banks' section",
    
    // Removed mapping translations - will be replaced with new logic
    
    // Missing Reference Codes Dialog
    missingReferenceCodes: "Missing Reference Codes",
    missingCodesDescription: "The CSV file cannot be processed because it contains reference codes that do not exist in the system.",
    missingCodesList: "Missing codes:",
    addMissingCodes: "Add Missing Codes",
    goToReferenceCodes: "Go to Reference Codes",
    closeMissingCodes: "Close",
    
    // Reference Codes
    manageReferenceCodes: "Manage Reference Codes",
    addCode: "Add Code",
    operativeCode: "Operative Code",
    description: "Description",
    actions: "Actions",
    edit: "Edit",
    delete: "Delete",
    save: "Save",
    cancel: "Cancel",
    codeExists: "Code already exists",
    
    // Processed Data
    bankReconciliation: "Bank Reconciliation",
    order: "Order",
    processed: "Processed",
    date: "Date",
    type: "Type",
    concept: "Concept",
    debits: "Debits",
    credits: "Credits",
    balance: "Balance",
    totalDebits: "Total Debits",
    totalCredits: "Total Credits",
    
    // Settings
    languageSettings: "Language Settings",
    selectLanguage: "Select Language",
    spanish: "Español",
    english: "English",
    profileSettings: "Profile Settings",
    changePassword: "Change Password",
    changeEmail: "Change Email",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    newEmail: "New Email",
    resetDatabase: "Reset Database",
    dangerZone: "Danger Zone",
    resetDatabaseWarning: "WARNING! This action will delete all bank movements and reference codes data. This action cannot be undone.",
    confirmReset: "Confirm Reset",
    enterPasswordToConfirm: "Enter your password to confirm",
    
    // General
    loading: "Loading...",
    noData: "No data available",
    error: "Error",
    success: "Success"
  }
};

// Auth Context
const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [language, setLanguage] = useState('es');

  useEffect(() => {
    if (token) {
      // Set default axios header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser({ username: 'admin' }); // Simple user object
      
      // Load language preference
      loadLanguage();
    }
  }, [token]);

  const loadLanguage = async () => {
    try {
      const response = await axios.get(`${API}/settings/language`);
      setLanguage(response.data.language);
    } catch (error) {
      console.error('Error loading language:', error);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { token: newToken, username: user } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser({ username: user });
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const changeLanguage = async (newLanguage) => {
    try {
      await axios.post(`${API}/settings/language`, { language: newLanguage });
      setLanguage(newLanguage);
      toast.success(translations[newLanguage].success);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, language, changeLanguage }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Login Component
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, language } = useAuth();
  const t = translations[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const success = await login(username, password);
    if (!success) {
      toast.error(t.invalidCredentials);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-blue-900">
            Conciliaciones Bancarias
          </CardTitle>
          <CardDescription>{t.login}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">{t.username}</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t.password}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t.loading : t.loginButton}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab }) => {
  const { logout, language } = useAuth();
  const t = translations[language];

  const menuItems = [
    { id: 'upload', icon: Upload, label: t.uploadData },
    { id: 'codes', icon: FileText, label: t.referenceCodes },
    { id: 'banks', icon: Building2, label: t.bankManagement },
    { id: 'data', icon: CheckCircle, label: t.processedData },
    { id: 'settings', icon: Settings, label: t.settings },
  ];

  return (
    <div className="w-64 bg-blue-900 text-white h-screen flex flex-col">
      <div className="p-6">
        <h1 className="text-xl font-bold">Conciliaciones</h1>
        <p className="text-blue-200 text-sm">Bancarias</p>
      </div>
      
      <nav className="flex-1 px-4">
        {menuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
              activeTab === item.id
                ? 'bg-blue-700 text-white'
                : 'text-blue-200 hover:bg-blue-800 hover:text-white'
            }`}
          >
            <item.icon size={20} />
            {item.label}
          </button>
        ))}
      </nav>
      
      <div className="p-4">
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-blue-200 hover:bg-blue-800 hover:text-white transition-colors"
        >
          <LogOut size={20} />
          {t.logout}
        </button>
      </div>
    </div>
  );
};

// Upload Component
const UploadTab = () => {
  const [file, setFile] = useState(null);
  const [banco, setBanco] = useState('');
  const [loading, setLoading] = useState(false);
  const [newCodes, setNewCodes] = useState([]);
  const [showNewCodes, setShowNewCodes] = useState(false);
  const [managedBanks, setManagedBanks] = useState([]);
  
  // New manual mapping state variables
  const [showManualMapping, setShowManualMapping] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [csvDelimiter, setCsvDelimiter] = useState(',');
  const [manualMappings, setManualMappings] = useState({});
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [csvContent, setCsvContent] = useState('');
  
  const [showMissingCodesDialog, setShowMissingCodesDialog] = useState(false);
  const [missingCodesData, setMissingCodesData] = useState(null);
  const { language } = useAuth();
  const t = translations[language];

  useEffect(() => {
    loadManagedBanks();
  }, []);

  const loadManagedBanks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bank-management`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setManagedBanks(response.data);
    } catch (error) {
      console.error('Error loading managed banks:', error);
      // If no managed banks, we'll show a message to create them
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
    } else {
      toast.error('Por favor selecciona un archivo CSV válido');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Por favor selecciona un archivo');
      return;
    }
    
    if (!banco) {
      toast.error('Por favor selecciona un banco');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('banco', banco);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/csv`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success(t.uploadSuccess);
      setNewCodes(response.data.new_codes);
      if (response.data.new_codes.length > 0) {
        setShowNewCodes(true);
      }
      setFile(null);
      setBanco('');
    } catch (error) {
      // Check if CSV needs manual mapping (422 or format error)
      if (error.response?.status === 422 || 
          (typeof error.response?.data?.detail === 'string' && error.response.data.detail.includes('formato'))) {
        // Standard upload failed - trigger manual mapping analysis
        await analyzeCSVStructure();
        return;
      }
      // Check if error is missing reference codes (status 400)
      else if (error.response?.status === 400 && error.response?.data?.detail?.error_type === 'missing_reference_codes') {
        const errorDetail = error.response.data.detail;
        setMissingCodesData(errorDetail);
        setShowMissingCodesDialog(true);
      }
      // Check if error is duplicate data (status 409)
      else if (error.response?.status === 409 && error.response?.data?.detail?.error_type === 'duplicate_data') {
        const errorDetail = error.response.data.detail;
        toast.error(
          `⚠️ Datos Duplicados: ${errorDetail.message}\n\n` +
          `📅 ${errorDetail.details}`, 
          { duration: 8000 }
        );
      } else {
        toast.error(error.response?.data?.detail || t.uploadError);
      }
      console.error('Upload error:', error);
    } finally {
      setLoading(false);
    }
  };
  // New manual mapping functions
  const analyzeCSVStructure = async () => {
    console.log('Analyzing CSV Structure - File:', file, 'Banco:', banco);
    
    if (!file || !banco) {
      console.error('Missing file or banco:', { file, banco });
      toast.error('Falta seleccionar archivo o banco');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('banco', banco);
      
      const apiUrl = `${API}/csv/analyze`;
      console.log('Making CSV analysis request to:', apiUrl);
      console.log('FormData contents:', { file: file.name, banco, fileSize: file.size });
      
      const response = await axios.post(
        apiUrl,
        formData,
        { 
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      console.log('CSV analysis response:', response.status, response.data);
      
      if (response.status === 200) {
        const result = response.data;
        
        // Store CSV content for later processing
        const fileContent = await file.text();
        setCsvContent(btoa(unescape(encodeURIComponent(fileContent))));
        
        if (result.action === "auto_apply_mapping") {
          // Auto-apply existing mapping
          toast.success(`🎯 Mapeo guardado encontrado! Aplicando automáticamente...`);
          
          // Set the existing mappings and process automatically
          setManualMappings(result.existing_mappings);
          setDateFormat(result.date_format);
          setCsvDelimiter(result.delimiter);
          
          // Process with existing mappings automatically
          const requestData = {
            banco: banco,
            csv_content: csvContent,
            mappings: result.existing_mappings,
            date_format: result.date_format,
            delimiter: result.delimiter
          };
          
          // Process immediately without showing mapping interface
          processWithSavedMappings(requestData);
          
        } else {
          // Show manual mapping interface
          setCsvHeaders(result.headers);
          setSampleRows(result.sample_rows);
          setCsvDelimiter(result.delimiter);
          setShowManualMapping(true);
          toast.success('CSV analizado. Configure el mapeo de columnas.');
        }
      }
    } catch (error) {
      console.error('Manual Mapping Processing Error:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      let errorMessage = 'Error procesando el archivo con mapeo manual';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage += `: ${JSON.stringify(error.response.data.detail)}`;
        }
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      // Show detailed error in console and toast
      console.error('FULL ERROR DETAILS:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        stack: error.stack
      });
      
      toast.error(errorMessage, { duration: 10000 });
    } finally {
      setLoading(false);
    }
  };

  const updateMapping = (field, csvColumn) => {
    setManualMappings(prev => ({
      ...prev,
      [field]: csvColumn === "no_mapping" ? null : csvColumn
    }));
  };

  const validateMappings = () => {
    const required = ['fecha', 'codigo_operativo', 'concepto', 'saldo'];
    const missing = required.filter(field => !manualMappings[field]);
    
    // Check amount fields
    const hasDebitos = manualMappings.debitos;
    const hasCreditos = manualMappings.creditos;
    const hasImporte = manualMappings.importe;
    
    if (!hasDebitos && !hasCreditos && !hasImporte) {
      missing.push('amount_field');
    }
    
    return missing;
  };

  const processManualMapping = async () => {
    const missingFields = validateMappings();
    if (missingFields.length > 0) {
      toast.error(`Campos requeridos faltantes: ${missingFields.join(', ')}`);
      return;
    }
    
    setLoading(true);
    try {
      const requestData = {
        banco: banco,
        csv_content: csvContent,
        mappings: manualMappings,
        date_format: dateFormat,
        delimiter: csvDelimiter
      };
      
      const response = await axios.post(
        `${API}/csv/process-mapping`,
        requestData,
        { 
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        const result = response.data;
        console.log('Processing successful response:', result);
        
        // Show success message with movement count
        toast.success(
          `✅ ${t.uploadSuccess}\n` +
          `📊 ${result.movements_count || 0} movimientos procesados\n` +
          `💰 Débitos: ${result.total_debitos || 0}\n` +
          `💰 Créditos: ${result.total_creditos || 0}\n` +
          `📈 Saldo: ${result.saldo_disponible || 0}`,
          { duration: 8000 }
        );
        
        // Reset states
        setShowManualMapping(false);
        setFile(null);
        setBanco('');
        setCsvHeaders([]);
        setSampleRows([]);
        setManualMappings({});
        setCsvContent('');
        
        // Optionally, you can add a note to check processed data
        setTimeout(() => {
          toast.info('📋 Los datos procesados están disponibles en la pestaña "Datos Procesados"', { duration: 5000 });
        }, 2000);
      } else {
        console.warn('Unexpected response status:', response.status);
        toast.error(`Respuesta inesperada: ${response.status}`);
      }
    } catch (error) {
      // Check for missing reference codes
      if (error.response?.status === 400 && error.response?.data?.detail?.error_type === 'missing_reference_codes') {
        const errorDetail = error.response.data.detail;
        setMissingCodesData(errorDetail);
        setShowMissingCodesDialog(true);
      }
      // Check for duplicate data
      else if (error.response?.status === 409 && error.response?.data?.detail?.error_type === 'duplicate_data') {
        const errorDetail = error.response.data.detail;
        toast.error(
          `⚠️ Datos Duplicados: ${errorDetail.message}\n\n` +
          `📅 ${errorDetail.details}`, 
          { duration: 8000 }
        );
      } else {
        let errorMessage = 'Error procesando el archivo con mapeo manual';
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.message) {
          errorMessage += `: ${error.message}`;
        }
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const processWithSavedMappings = async (requestData) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/csv/process-mapping`,
        requestData,
        { 
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        const result = response.data;
        
        toast.success(
          `✅ Procesado automáticamente con mapeo guardado!\n` +
          `📊 ${result.movements_count || 0} movimientos procesados\n` +
          `💰 Débitos: ${result.total_debitos || 0}\n` +
          `💰 Créditos: ${result.total_creditos || 0}\n` +
          `📈 Saldo: ${result.saldo_disponible || 0}`,
          { duration: 8000 }
        );
        
        // Reset states
        setFile(null);
        setBanco('');
        setCsvContent('');
        
        // Guide user to processed data
        setTimeout(() => {
          toast.info('📋 Los datos procesados están disponibles en la pestaña "Datos Procesados"', { duration: 5000 });
        }, 2000);
      }
    } catch (error) {
      // Handle errors same as manual mapping
      if (error.response?.status === 400 && error.response?.data?.detail?.error_type === 'missing_reference_codes') {
        const errorDetail = error.response.data.detail;
        setMissingCodesData(errorDetail);
        setShowMissingCodesDialog(true);
      } else if (error.response?.status === 409 && error.response?.data?.detail?.error_type === 'duplicate_data') {
        const errorDetail = error.response.data.detail;
        toast.error(
          `⚠️ Datos Duplicados: ${errorDetail.message}\n\n` +
          `📅 ${errorDetail.details}`, 
          { duration: 8000 }
        );
      } else {
        toast.error('Error procesando archivo con mapeo guardado');
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelManualMapping = () => {
    setShowManualMapping(false);
    setCsvHeaders([]);
    setSampleRows([]);
    setManualMappings({});
    setCsvContent('');
    setFile(null);
    setBanco('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{t.uploadCSV}</h2>
        <p className="text-gray-600 mt-2">Sube tu archivo de movimientos bancarios en formato CSV</p>
      </div>

      <Card>
        <CardContent className="p-6 space-y-6">
          {/* Bank Selection */}
          <div>
            <Label className="text-base font-medium mb-3 block">{t.bankName}</Label>
            {managedBanks.length > 0 ? (
              <Select value={banco} onValueChange={setBanco}>
                <SelectTrigger className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <SelectValue placeholder={t.selectBank} />
                </SelectTrigger>
                <SelectContent>
                  {managedBanks.map((bank) => (
                    <SelectItem key={bank.id} value={bank.name}>
                      {bank.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="space-y-3">
                <div className="text-center p-3 border border-yellow-300 rounded-lg bg-yellow-50">
                  <p className="text-yellow-700 text-sm">{t.noBanksFound}</p>
                  <p className="text-xs text-yellow-600">{t.addBanksFirst}</p>
                </div>
                <Input
                  type="text"
                  placeholder={t.bankPlaceholder}
                  value={banco}
                  onChange={(e) => setBanco(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>

          {/* File Upload Section */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <Label htmlFor="file-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  {t.dragDropFile}
                </span>
                <Input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  accept=".csv"
                  className="sr-only"
                  onChange={handleFileChange}
                />
              </Label>
            </div>
            {file && (
              <div className="mt-4">
                <p className="text-sm text-gray-500">Archivo seleccionado: {file.name}</p>
                <p className="text-sm text-blue-600">Banco seleccionado: {banco}</p>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <Button 
            onClick={handleUpload} 
            disabled={loading || !file || !banco} 
            className="w-full"
          >
            {loading ? t.processing : t.uploadFile}
          </Button>
        </CardContent>
      </Card>

      {/* Manual CSV Column Mapping Interface */}
      {showManualMapping && (
        <Card className="mt-6 border-2 border-blue-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Settings className="w-5 h-5" />
              Mapeo Manual de Columnas CSV
            </CardTitle>
            <CardDescription>
              Configure cómo se mapean las columnas del CSV a los campos de Datos Procesados
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* CSV Headers Preview */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h5 className="font-medium text-gray-900 mb-2">Columnas encontradas en el CSV:</h5>
              <div className="flex flex-wrap gap-2">
                {csvHeaders.map((header, index) => (
                  <Badge key={index} variant="outline" className="font-mono">
                    {header}
                  </Badge>
                ))}
              </div>
              {sampleRows.length > 0 && (
                <div className="mt-3">
                  <h6 className="text-sm font-medium text-gray-700 mb-1">Datos de ejemplo:</h6>
                  <div className="text-xs font-mono bg-white p-2 rounded border max-h-20 overflow-y-auto">
                    {sampleRows.slice(0, 2).map((row, idx) => (
                      <div key={idx} className="truncate text-gray-600">
                        {row.slice(0, 5).join(' | ')}...
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Date Format Selection */}
            <div className="p-4 bg-blue-50 rounded-lg">
              <Label className="text-blue-900 font-medium">Formato de Fecha en el CSV</Label>
              <p className="text-sm text-blue-700 mb-3">Seleccione el formato usado en su archivo CSV</p>
              <Select value={dateFormat} onValueChange={setDateFormat}>
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (31/12/2023)</SelectItem>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (12/31/2023)</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (2023-12-31)</SelectItem>
                  <SelectItem value="DD-MM-YYYY">DD-MM-YYYY (31-12-2023)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Column Mapping */}
            <div className="space-y-4">
              <h5 className="font-medium text-gray-900">Mapeo de Columnas:</h5>
              
              {/* Required Fields */}
              <div className="grid gap-4">
                {[
                  { field: 'fecha', label: 'Fecha', required: true, description: 'Fecha de la transacción' },
                  { field: 'codigo_operativo', label: 'Código Operativo', required: true, description: 'Código que se validará en Referencias' },
                  { field: 'concepto', label: 'Concepto', required: true, description: 'Descripción de la transacción' },
                  { field: 'debitos', label: 'Débitos', required: false, description: 'Columna de montos negativos (opcional si hay Importe)' },
                  { field: 'creditos', label: 'Créditos', required: false, description: 'Columna de montos positivos (opcional si hay Importe)' },
                  { field: 'importe', label: 'Importe', required: false, description: 'Columna única de montos (opcional si hay Débitos/Créditos)' },
                  { field: 'saldo', label: 'Saldo', required: true, description: 'Saldo de la cuenta después de la transacción' }
                ].map(fieldDef => (
                  <div key={fieldDef.field} className="flex items-center gap-4 p-3 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Label className="font-medium">
                          {fieldDef.label}
                          {fieldDef.required && <span className="text-red-500">*</span>}
                        </Label>
                        {manualMappings[fieldDef.field] && (
                          <Badge variant="secondary" className="text-xs">
                            {manualMappings[fieldDef.field]}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{fieldDef.description}</p>
                    </div>
                    <div className="w-48">
                      <Select 
                        value={manualMappings[fieldDef.field] || "no_mapping"} 
                        onValueChange={(value) => updateMapping(fieldDef.field, value)}
                      >
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no_mapping" className="text-gray-500">
                            — No mapear —
                          </SelectItem>
                          {csvHeaders.map((header) => (
                            <SelectItem key={header} value={header}>
                              {header}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Validation Status */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h5 className="font-medium text-gray-900 mb-2">Estado de Validación:</h5>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[
                  { field: 'fecha', label: 'Fecha' },
                  { field: 'codigo_operativo', label: 'Código Operativo' },
                  { field: 'concepto', label: 'Concepto' },
                  { field: 'saldo', label: 'Saldo' }
                ].map(({ field, label }) => {
                  const isMapped = manualMappings[field];
                  return (
                    <div key={field} className={`flex items-center gap-2 ${isMapped ? 'text-green-700' : 'text-red-700'}`}>
                      <span>{isMapped ? '✓' : '✗'}</span>
                      <span>{label}</span>
                    </div>
                  );
                })}
                <div className={`flex items-center gap-2 ${
                  (manualMappings.debitos || manualMappings.creditos || manualMappings.importe) ? 'text-green-700' : 'text-red-700'
                }`}>
                  <span>{(manualMappings.debitos || manualMappings.creditos || manualMappings.importe) ? '✓' : '✗'}</span>
                  <span>Montos (Débitos/Créditos/Importe)</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button 
                onClick={processManualMapping}
                disabled={loading || validateMappings().length > 0}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Procesando...
                  </>
                ) : (
                  'Procesar con Mapeo Manual'
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={cancelManualMapping}
                disabled={loading}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showNewCodes && newCodes.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{t.newCodesFound}:</strong> {newCodes.join(', ')}
            <br />
            <span className="text-sm text-gray-600">{t.addNewCodes}</span>
          </AlertDescription>
        </Alert>
      )}

      {/* Missing Reference Codes Dialog */}
      <Dialog open={showMissingCodesDialog} onOpenChange={(open) => {
        // Only allow closing via button clicks, not by clicking outside
        if (!open) return;
        setShowMissingCodesDialog(open);
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {t.missingReferenceCodes}
            </DialogTitle>
            <DialogDescription>
              {t.missingCodesDescription}
            </DialogDescription>
          </DialogHeader>
          
          {missingCodesData && (
            <div className="space-y-4">
              {/* Error Message */}
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium">{missingCodesData.message}</p>
              </div>

              {/* Missing Codes List */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">{t.missingCodesList}</h4>
                <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                  {missingCodesData.missing_codes?.map((code, index) => (
                    <div key={index} className="bg-gray-100 px-3 py-2 rounded font-mono text-sm">
                      {code}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  <strong>Total:</strong> {missingCodesData.total_missing} códigos faltantes
                </p>
              </div>

              {/* Action Required */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 font-medium">⚡ {missingCodesData.action_required}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={() => {
                    setShowMissingCodesDialog(false);
                    // You could add navigation to reference codes tab here
                    // setActiveTab('codes');
                  }}
                  className="flex-1"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t.goToReferenceCodes}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowMissingCodesDialog(false)}
                  className="flex-1"
                >
                  {t.closeMissingCodes}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Reference Codes Component
const ReferenceCodesTab = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({ cod_operativo: '', description: '' });
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const { language } = useAuth();
  const t = translations[language];

  useEffect(() => {
    loadCodes();
  }, []);

  const loadCodes = async () => {
    try {
      const response = await axios.get(`${API}/reference-codes`);
      setCodes(response.data);
    } catch (error) {
      console.error('Error loading codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      if (editingCode) {
        await axios.put(`${API}/reference-codes/${editingCode.id}`, formData, { headers });
        toast.success('Código actualizado');
      } else {
        await axios.post(`${API}/reference-codes`, formData, { headers });
        toast.success('Código creado');
      }
      setShowDialog(false);
      setEditingCode(null);
      setFormData({ cod_operativo: '', description: '' });
      loadCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({ cod_operativo: code.cod_operativo, description: code.description });
    setShowDialog(true);
  };

  const handleDelete = async (codeId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este código?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/reference-codes/${codeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Código eliminado');
        loadCodes();
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  const handleBulkFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setBulkFile(selectedFile);
    } else {
      toast.error('Por favor selecciona un archivo CSV válido');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;

    setBulkUploading(true);
    const formData = new FormData();
    formData.append('file', bulkFile);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/reference-codes/bulk-upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = response.data;
      toast.success(
        `${result.message}: ${result.added_count} agregados, ${result.updated_count} actualizados, ${result.error_count} errores`
      );
      
      if (result.errors.length > 0) {
        console.warn('Errores en la carga:', result.errors);
      }
      
      setShowBulkUpload(false);
      setBulkFile(null);
      loadCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en la carga masiva');
    } finally {
      setBulkUploading(false);
    }
  };

  if (loading) return <div className="p-8 text-center">{t.loading}</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t.manageReferenceCodes}</h2>
          <p className="text-gray-600 mt-2">Gestiona los códigos operativos y sus descripciones</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Upload className="w-4 h-4 mr-2" />
                Cargar CSV
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Carga Masiva de Códigos</DialogTitle>
                <DialogDescription>
                  Sube un archivo CSV con códigos operativos. Formato: cod_operativo,descripcion
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FileText className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <div>
                    <Label htmlFor="bulk-file-upload" className="cursor-pointer">
                      <span className="text-sm font-medium text-gray-900">
                        Selecciona tu archivo CSV de códigos
                      </span>
                      <Input
                        id="bulk-file-upload"
                        name="bulk-file-upload"
                        type="file"
                        accept=".csv"
                        className="sr-only"
                        onChange={handleBulkFileChange}
                      />
                    </Label>
                  </div>
                  {bulkFile && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">Archivo: {bulkFile.name}</p>
                    </div>
                  )}
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Formato del CSV:</h4>
                  <pre className="text-sm text-blue-800 bg-blue-100 p-2 rounded">
{`cod_operativo,descripcion
101,CHRE
108,CHRE
4085,DA
1932,GNGR`}
                  </pre>
                  <p className="text-xs text-blue-700 mt-2">
                    • Los códigos existentes se actualizarán<br/>
                    • Se pueden usar comas o punto y coma como separadores
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleBulkUpload} 
                    disabled={!bulkFile || bulkUploading}
                    className="flex-1"
                  >
                    {bulkUploading ? 'Procesando...' : 'Cargar Códigos'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowBulkUpload(false);
                      setBulkFile(null);
                    }}
                  >
                    {t.cancel}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setEditingCode(null);
                setFormData({ cod_operativo: '', description: '' });
              }}>
                <Plus className="w-4 h-4 mr-2" />
                {t.addCode}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingCode ? t.edit : t.addCode} {t.operativeCode}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t.operativeCode}</Label>
                  <Input
                    value={formData.cod_operativo}
                    onChange={(e) => setFormData({ ...formData, cod_operativo: e.target.value })}
                  />
                </div>
                <div>
                  <Label>{t.description}</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSave}>{t.save}</Button>
                  <Button variant="outline" onClick={() => setShowDialog(false)}>
                    {t.cancel}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.operativeCode}</TableHead>
                <TableHead>{t.description}</TableHead>
                <TableHead className="w-24">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes.map((code) => (
                <TableRow key={code.id}>
                  <TableCell className="font-mono">{code.cod_operativo}</TableCell>
                  <TableCell>{code.description}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(code)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(code.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {codes.length === 0 && (
            <div className="p-8 text-center text-gray-500">{t.noData}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Bank Management Tab
const BankManagementTab = () => {
  const { language } = useAuth();
  const t = translations[language];
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ name: '', code: '' });
  const [editingBank, setEditingBank] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadBanks();
  }, []);

  const loadBanks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/bank-management`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBanks(response.data);
    } catch (error) {
      console.error('Error loading banks:', error);
      toast.error('Error al cargar los bancos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('El nombre del banco es requerido');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      if (editingBank) {
        await axios.put(`${API}/bank-management/${editingBank.id}`, formData, { headers });
        toast.success('Banco actualizado exitosamente');
      } else {
        await axios.post(`${API}/bank-management`, formData, { headers });
        toast.success('Banco creado exitosamente');
      }

      setFormData({ name: '', code: '' });
      setEditingBank(null);
      setShowForm(false);
      loadBanks();
    } catch (error) {
      console.error('Error saving bank:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el banco');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (bank) => {
    setEditingBank(bank);
    setFormData({ name: bank.name, code: bank.code || '' });
    setShowForm(true);
  };

  const handleDelete = async (bankId, bankName) => {
    if (!confirm(`¿Está seguro de eliminar el banco "${bankName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/bank-management/${bankId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Banco eliminado exitosamente');
      loadBanks();
    } catch (error) {
      console.error('Error deleting bank:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el banco');
    }
  };

  const cancelEdit = () => {
    setEditingBank(null);
    setFormData({ name: '', code: '' });
    setShowForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Gestión de Bancos</h2>
          <p className="text-gray-600 mt-2">Administra la lista de bancos disponibles para cargar datos</p>
        </div>
        <Button onClick={() => setShowForm(true)} disabled={loading}>
          <Plus className="w-4 h-4 mr-2" />
          Agregar Banco
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingBank ? 'Editar Banco' : 'Agregar Nuevo Banco'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nombre del Banco *</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ej: Banco Santander"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="code">Código (Opcional)</Label>
                  <Input
                    id="code"
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    placeholder="Ej: 072"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Guardando...' : (editingBank ? 'Actualizar' : 'Crear')}
                </Button>
                <Button type="button" variant="outline" onClick={cancelEdit}>
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Banks List */}
      <Card>
        <CardHeader>
          <CardTitle>Bancos Registrados ({banks.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p>Cargando bancos...</p>
            </div>
          ) : banks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No hay bancos registrados</p>
              <p className="text-sm">Agrega el primer banco para comenzar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Fecha Creación</TableHead>
                    <TableHead className="w-24">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {banks.map((bank) => (
                    <TableRow key={bank.id}>
                      <TableCell className="font-medium">{bank.name}</TableCell>
                      <TableCell className="font-mono text-sm">{bank.code || '-'}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(bank.created_at).toLocaleDateString('es-AR')}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(bank)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(bank.id, bank.name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Processed Data Component
const ProcessedDataTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [filters, setFilters] = useState({
    fecha_desde: '',
    fecha_hasta: '',
    tipo: '',
    banco: ''
  });
  const [editingMovement, setEditingMovement] = useState(null);
  const [editingTipo, setEditingTipo] = useState('');
  const [showCustomTipo, setShowCustomTipo] = useState(false);
  const [customTipo, setCustomTipo] = useState('');
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [deleteForm, setDeleteForm] = useState({
    banco: '',
    fecha_desde: '',
    fecha_hasta: '',
    confirmation_text: ''
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const { language } = useAuth();
  const t = translations[language];

  useEffect(() => {
    loadProcessedData();
    loadFilterOptions();
  }, []);

  const loadProcessedData = async () => {
    try {
      const response = await axios.get(`${API}/processed-data`);
      setData(response.data);
    } catch (error) {
      console.error('Error loading processed data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const [banksRes, tiposRes, refCodesRes] = await Promise.all([
        axios.get(`${API}/banks`),
        axios.get(`${API}/tipos`),
        axios.get(`${API}/reference-codes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      setBanks(banksRes.data.banks || []);
      setTipos(tiposRes.data.tipos || []);
      
      // Extract unique descriptions from reference codes for dropdown
      const referenceDescriptions = [...new Set(
        refCodesRes.data
          .map(code => code.description)
          .filter(desc => desc && desc !== 'NA')
      )].sort();
      
      // Combine with existing tipos and remove duplicates
      const allTipos = [...new Set([...tiposRes.data.tipos || [], ...referenceDescriptions])].sort();
      setTipos(allTipos);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const applyFilters = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/processed-data/filter`, filters, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error applying filters:', error);
      toast.error('Error al aplicar filtros');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      fecha_desde: '',
      fecha_hasta: '',
      tipo: '',
      banco: ''
    });
    loadProcessedData();
  };

  const handleEditTipo = (movement) => {
    setEditingMovement(movement.id);
    setEditingTipo(movement.tipo || '');
    setShowCustomTipo(false);
    setCustomTipo('');
  };

  const handleSaveTipo = async (movementId) => {
    try {
      const token = localStorage.getItem('token');
      const finalTipo = showCustomTipo ? customTipo : editingTipo;
      
      if (!finalTipo.trim()) {
        toast.error('Por favor selecciona o escribe un tipo');
        return;
      }
      
      await axios.put(`${API}/processed-data/update-tipo`, {
        movement_id: movementId,
        new_tipo: finalTipo
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Update local data
      setData(prev => ({
        ...prev,
        movements: prev.movements.map(m => 
          m.id === movementId ? { ...m, tipo: finalTipo } : m
        )
      }));

      setEditingMovement(null);
      setEditingTipo('');
      setShowCustomTipo(false);
      setCustomTipo('');
      toast.success('Tipo actualizado exitosamente');
    } catch (error) {
      console.error('Error updating tipo:', error);
      toast.error('Error al actualizar el tipo');
    }
  };

  const handleCancelEdit = () => {
    setEditingMovement(null);
    setEditingTipo('');
    setShowCustomTipo(false);
    setCustomTipo('');
  };

  const handleToggleHighlight = async (movementId, currentHighlight) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/processed-data/toggle-highlight`, {
        movement_id: movementId,
        highlighted: !currentHighlight
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Update local data
      setData(prev => ({
        ...prev,
        movements: prev.movements.map(m => 
          m.id === movementId ? { ...m, highlighted: !currentHighlight } : m
        )
      }));

      toast.success(currentHighlight ? 'Resaltado removido' : 'Fila resaltada para verificación');
    } catch (error) {
      console.error('Error toggling highlight:', error);
      toast.error('Error al cambiar el resaltado');
    }
  };

  const handleDeleteRange = async () => {
    if (!deleteForm.banco || !deleteForm.fecha_desde || !deleteForm.fecha_hasta) {
      toast.error('Por favor complete todos los campos requeridos');
      return;
    }

    if (deleteForm.confirmation_text !== 'CONFIRMAR ELIMINACION') {
      toast.error('Debe escribir exactamente: CONFIRMAR ELIMINACION');
      return;
    }

    if (new Date(deleteForm.fecha_desde) > new Date(deleteForm.fecha_hasta)) {
      toast.error('La fecha desde no puede ser mayor que la fecha hasta');
      return;
    }

    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/processed-data/delete-range`, {
        headers: { 'Authorization': `Bearer ${token}` },
        data: deleteForm
      });

      toast.success(`✅ ${response.data.message}`, { duration: 6000 });
      
      // Reset form and close section
      setDeleteForm({
        banco: '',
        fecha_desde: '',
        fecha_hasta: '',
        confirmation_text: ''
      });
      setShowDeleteSection(false);

      // Reload data to reflect changes
      loadProcessedData();
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al eliminar los datos';
      toast.error(`❌ ${errorMessage}`, { duration: 8000 });
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteForm({
      banco: '',
      fecha_desde: '',
      fecha_hasta: '',
      confirmation_text: ''
    });
    setShowDeleteSection(false);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedMovements = (movements) => {
    if (!sortConfig.key) return movements;

    return [...movements].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle different data types
      switch (sortConfig.key) {
        case 'processed_at':
          aValue = new Date(aValue);
          bValue = new Date(bValue);
          break;
        case 'fecha':
          // Convert DD/MM/YYYY to Date object for proper sorting
          if (a.fecha_parsed && b.fecha_parsed) {
            aValue = new Date(a.fecha_parsed);
            bValue = new Date(b.fecha_parsed);
          } else {
            // Fallback to string parsing if fecha_parsed not available
            const [aDay, aMonth, aYear] = aValue.split('/');
            const [bDay, bMonth, bYear] = bValue.split('/');
            aValue = new Date(aYear, aMonth - 1, aDay);
            bValue = new Date(bYear, bMonth - 1, bDay);
          }
          break;
        case 'banco':
        case 'tipo':
          // String comparison (case insensitive)
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
          break;
        default:
          // Default string comparison
          aValue = (aValue || '').toString();
          bValue = (bValue || '').toString();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400 ml-1">↕️</span>;
    }
    return sortConfig.direction === 'asc' 
      ? <span className="text-blue-600 ml-1">↑</span>
      : <span className="text-blue-600 ml-1">↓</span>;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount);
  };

  if (loading) return <div className="p-8 text-center">{t.loading}</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{t.bankReconciliation}</h2>
        <p className="text-gray-600 mt-2">Últimos movimientos procesados</p>
      </div>

      {/* Filters Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Fecha Desde</Label>
              <Input
                type="date"
                value={filters.fecha_desde}
                onChange={(e) => setFilters({...filters, fecha_desde: e.target.value})}
              />
            </div>
            <div>
              <Label>Fecha Hasta</Label>
              <Input
                type="date"
                value={filters.fecha_hasta}
                onChange={(e) => setFilters({...filters, fecha_hasta: e.target.value})}
              />
            </div>
            <div>
              <Label>Banco</Label>
              <select
                value={filters.banco}
                onChange={(e) => setFilters({...filters, banco: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">Todos los bancos</option>
                {banks.map((bank) => (
                  <option key={bank} value={bank}>{bank}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Tipo de Movimiento</Label>
              <select
                value={filters.tipo}
                onChange={(e) => setFilters({...filters, tipo: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">Todos los tipos</option>
                {tipos.map((tipo) => (
                  <option key={tipo} value={tipo}>{tipo}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={applyFilters} disabled={loading}>
              <Search className="w-4 h-4 mr-2" />
              Aplicar Filtros
            </Button>
            <Button variant="outline" onClick={clearFilters} disabled={loading}>
              <X className="w-4 h-4 mr-2" />
              Limpiar
            </Button>
            {sortConfig.key && (
              <Button 
                variant="outline" 
                onClick={() => setSortConfig({ key: null, direction: 'asc' })}
                className="text-blue-600 border-blue-300"
              >
                <ArrowUpDown className="w-4 h-4 mr-2" />
                Limpiar Orden
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Data Section */}
      <Card className="border-red-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-medium text-red-800">Eliminar Datos por Rango</h3>
            </div>
            <Button
              variant={showDeleteSection ? "destructive" : "outline"}
              size="sm"
              onClick={() => setShowDeleteSection(!showDeleteSection)}
            >
              {showDeleteSection ? 'Cancelar' : 'Eliminar Datos'}
            </Button>
          </div>

          {showDeleteSection && (
            <div className="border-t pt-4 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-800">⚠️ Advertencia</h4>
                    <p className="text-sm text-red-700 mt-1">
                      Esta acción eliminará permanentemente todos los movimientos del banco y rango de fechas especificado. 
                      <strong> Esta operación NO se puede deshacer.</strong>
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-red-800">Banco *</Label>
                  <select
                    value={deleteForm.banco}
                    onChange={(e) => setDeleteForm({...deleteForm, banco: e.target.value})}
                    className="w-full p-2 border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="">Seleccionar banco</option>
                    {banks.map((bank) => (
                      <option key={bank} value={bank}>{bank}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-red-800">Fecha Desde *</Label>
                  <Input
                    type="date"
                    value={deleteForm.fecha_desde}
                    onChange={(e) => setDeleteForm({...deleteForm, fecha_desde: e.target.value})}
                    className="border-red-300 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
                <div>
                  <Label className="text-red-800">Fecha Hasta *</Label>
                  <Input
                    type="date"
                    value={deleteForm.fecha_hasta}
                    onChange={(e) => setDeleteForm({...deleteForm, fecha_hasta: e.target.value})}
                    className="border-red-300 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              </div>

              <div>
                <Label className="text-red-800">Confirmación *</Label>
                <p className="text-sm text-gray-600 mb-2">
                  Para confirmar la eliminación, escriba exactamente: <code className="bg-gray-100 px-2 py-1 rounded">CONFIRMAR ELIMINACION</code>
                </p>
                <Input
                  type="text"
                  value={deleteForm.confirmation_text}
                  onChange={(e) => setDeleteForm({...deleteForm, confirmation_text: e.target.value})}
                  placeholder="Escriba: CONFIRMAR ELIMINACION"
                  className="border-red-300 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  variant="destructive"
                  onClick={handleDeleteRange}
                  disabled={deleteLoading || deleteForm.confirmation_text !== 'CONFIRMAR ELIMINACION'}
                  className="flex items-center gap-2"
                >
                  {deleteLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Eliminar Datos Permanentemente
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={cancelDelete}
                  disabled={deleteLoading}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {data && data.movements.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-gray-600">{t.totalDebits}</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(data.total_debitos)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-gray-600">{t.totalCredits}</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(data.total_creditos)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-sm font-medium text-gray-600">Saldo Disponible</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(data.saldo_disponible)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Results Summary */}
      {data && (
        <Card>
          <CardContent className="p-4">
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>Mostrando {data.movements.length} movimientos</span>
              <span>
                {filters.fecha_desde || filters.fecha_hasta || filters.tipo || filters.banco ? 
                  'Filtros aplicados' : 'Sin filtros'}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          {sortConfig.key && (
            <div className="p-3 bg-blue-50 border-b border-blue-200">
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <ArrowUpDown className="w-4 h-4" />
                <span>
                  Ordenado por: <strong>
                    {
                      sortConfig.key === 'processed_at' ? t.processed :
                      sortConfig.key === 'banco' ? 'Banco' :
                      sortConfig.key === 'fecha' ? t.date :
                      sortConfig.key === 'tipo' ? t.type : sortConfig.key
                    }
                  </strong> 
                  ({sortConfig.direction === 'asc' ? 'Ascendente' : 'Descendente'})
                </span>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead 
                    className="w-36 cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('processed_at')}
                  >
                    <div className="flex items-center">
                      {t.processed}
                      {getSortIcon('processed_at')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('banco')}
                  >
                    <div className="flex items-center">
                      Banco
                      {getSortIcon('banco')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('fecha')}
                  >
                    <div className="flex items-center">
                      {t.date}
                      {getSortIcon('fecha')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('tipo')}
                  >
                    <div className="flex items-center">
                      {t.type}
                      {getSortIcon('tipo')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="w-32 cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('cod_operativo')}
                  >
                    <div className="flex items-center">
                      {t.operativeCode}
                      {getSortIcon('cod_operativo')}
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[300px]">{t.concept}</TableHead>
                  <TableHead className="text-right">{t.debits}</TableHead>
                  <TableHead className="text-right">{t.credits}</TableHead>
                  <TableHead className="text-right">{t.balance}</TableHead>
                  <TableHead className="w-24">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data && getSortedMovements(data.movements).map((movement, index) => (
                  <TableRow 
                    key={movement.id}
                    className={movement.highlighted ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : ''}
                  >
                    <TableCell className="font-mono text-sm text-gray-500">
                      {index + 1}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      <div className="flex flex-col">
                        <span className="font-mono">
                          {movement.processed_at ? new Date(movement.processed_at).toLocaleDateString('es-AR', {
                            day: '2-digit',
                            month: '2-digit', 
                            year: 'numeric'
                          }) : '-'}
                        </span>
                        <span className="font-mono text-gray-400">
                          {movement.processed_at ? new Date(movement.processed_at).toLocaleTimeString('es-AR', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          }) : ''}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {movement.banco}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {movement.fecha}
                    </TableCell>
                    <TableCell>
                      {editingMovement === movement.id ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1">
                            {!showCustomTipo ? (
                              <>
                                <Select 
                                  value={editingTipo} 
                                  onValueChange={(value) => {
                                    if (value === '___CUSTOM___') {
                                      setShowCustomTipo(true);
                                      setEditingTipo('');
                                    } else {
                                      setEditingTipo(value);
                                    }
                                  }}
                                >
                                  <SelectTrigger className="h-8 w-[200px] text-sm">
                                    <SelectValue placeholder="Seleccionar tipo" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="SIN CLASIFICAR">SIN CLASIFICAR</SelectItem>
                                    {tipos.map((tipo) => (
                                      <SelectItem key={tipo} value={tipo}>
                                        {tipo}
                                      </SelectItem>
                                    ))}
                                    <SelectItem value="___CUSTOM___" className="text-blue-600 font-medium">
                                      ✏️ Escribir tipo personalizado
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveTipo(movement.id)}
                                  className="h-8 px-2"
                                  disabled={!editingTipo}
                                >
                                  ✓
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCancelEdit}
                                  className="h-8 px-2"
                                >
                                  ✕
                                </Button>
                              </>
                            ) : (
                              <>
                                <Input
                                  value={customTipo}
                                  onChange={(e) => setCustomTipo(e.target.value)}
                                  className="h-8 w-[200px] text-sm"
                                  placeholder="Escribir tipo personalizado"
                                  autoFocus
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveTipo(movement.id)}
                                  className="h-8 px-2"
                                  disabled={!customTipo.trim()}
                                >
                                  ✓
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setShowCustomTipo(false);
                                    setCustomTipo('');
                                  }}
                                  className="h-8 px-2"
                                >
                                  ↩️
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCancelEdit}
                                  className="h-8 px-2"
                                >
                                  ✕
                                </Button>
                              </>
                            )}
                          </div>
                          {showCustomTipo && (
                            <p className="text-xs text-gray-500">
                              💡 Escribe un tipo personalizado o usa ↩️ para volver al selector
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Badge variant={movement.tipo === 'SIN CLASIFICAR' ? 'destructive' : 'secondary'}>
                            {movement.tipo}
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditTipo(movement)}
                            className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                          >
                            ✏️
                          </Button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-center">
                      <Badge variant="outline" className="font-mono">
                        {movement.cod_operativo || '-'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {movement.concepto}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-600">
                      {movement.debitos ? formatCurrency(movement.debitos) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-green-600">
                      {movement.creditos ? formatCurrency(movement.creditos) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {movement.saldo_pesos}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant={movement.highlighted ? "default" : "outline"}
                        onClick={() => handleToggleHighlight(movement.id, movement.highlighted)}
                        className="h-8 w-8 p-0"
                        title={movement.highlighted ? "Remover resaltado" : "Resaltar para verificación"}
                      >
                        {movement.highlighted ? '🟡' : '⚪'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {(!data || data.movements.length === 0) && (
            <div className="p-8 text-center text-gray-500">{t.noData}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Settings Component
const SettingsTab = () => {
  const { language, changeLanguage } = useAuth();
  const [profile, setProfile] = useState({ username: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [emailForm, setEmailForm] = useState({
    new_email: '',
    password: ''
  });
  const [resetForm, setResetForm] = useState({
    password: '',
    confirmText: ''
  });
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const t = translations[language];

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/settings/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Contraseña actualizada exitosamente');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    
    if (!emailForm.new_email.includes('@')) {
      toast.error('Ingresa un email válido');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/settings/change-email`, {
        new_email: emailForm.new_email,
        password: emailForm.password
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Email actualizado exitosamente');
      setProfile({ ...profile, email: response.data.email });
      setEmailForm({ new_email: '', password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar email');
    } finally {
      setLoading(false);
    }
  };

  const handleResetDatabase = async () => {
    if (resetForm.confirmText !== 'CONFIRMAR') {
      toast.error('Debes escribir "CONFIRMAR" para continuar');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/reset-database`, {
        password: resetForm.password
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Base de datos reiniciada exitosamente');
      setShowResetDialog(false);
      setResetForm({ password: '', confirmText: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reiniciar base de datos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{t.settings}</h2>
        <p className="text-gray-600 mt-2">Configura las preferencias de la aplicación</p>
      </div>

      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            {t.profileSettings}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Usuario</Label>
              <Input value={profile.username} disabled className="bg-gray-50" />
            </div>
            <div>
              <Label>Email Actual</Label>
              <Input value={profile.email} disabled className="bg-gray-50" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle>{t.changePassword}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label>{t.currentPassword}</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>{t.newPassword}</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                required
                minLength={6}
              />
            </div>
            <div>
              <Label>{t.confirmPassword}</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                required
              />
            </div>
            <Button type="submit" disabled={loading}>
              {loading ? t.loading : t.changePassword}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Change Email */}
      <Card>
        <CardHeader>
          <CardTitle>{t.changeEmail}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangeEmail} className="space-y-4">
            <div>
              <Label>{t.newEmail}</Label>
              <Input
                type="email"
                value={emailForm.new_email}
                onChange={(e) => setEmailForm({...emailForm, new_email: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>{t.currentPassword}</Label>
              <Input
                type="password"
                value={emailForm.password}
                onChange={(e) => setEmailForm({...emailForm, password: e.target.value})}
                required
              />
            </div>
            <Button type="submit" disabled={loading}>
              {loading ? t.loading : t.changeEmail}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Language Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {t.languageSettings}
          </CardTitle>
          <CardDescription>
            {t.selectLanguage}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Button
              variant={language === 'es' ? 'default' : 'outline'}
              onClick={() => changeLanguage('es')}
            >
              {t.spanish}
            </Button>
            <Button
              variant={language === 'en' ? 'default' : 'outline'}
              onClick={() => changeLanguage('en')}
            >
              {t.english}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone - Reset Database */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            {t.dangerZone}
          </CardTitle>
          <CardDescription className="text-red-600">
            {t.resetDatabaseWarning}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
            <DialogTrigger asChild>
              <Button variant="destructive">
                {t.resetDatabase}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-red-600">
                  {t.confirmReset}
                </DialogTitle>
                <DialogDescription>
                  {t.resetDatabaseWarning}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t.enterPasswordToConfirm}</Label>
                  <Input
                    type="password"
                    value={resetForm.password}
                    onChange={(e) => setResetForm({...resetForm, password: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Escribe "CONFIRMAR" para continuar</Label>
                  <Input
                    value={resetForm.confirmText}
                    onChange={(e) => setResetForm({...resetForm, confirmText: e.target.value})}
                    placeholder="CONFIRMAR"
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="destructive" 
                    onClick={handleResetDatabase}
                    disabled={loading || resetForm.confirmText !== 'CONFIRMAR'}
                  >
                    {loading ? t.loading : t.confirmReset}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowResetDialog(false);
                      setResetForm({ password: '', confirmText: '' });
                    }}
                  >
                    {t.cancel}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Dashboard Component
const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('upload');

  const renderTabContent = () => {
    switch (activeTab) {
      case 'upload':
        return <UploadTab />;
      case 'codes':
        return <ReferenceCodesTab />;
      case 'banks':
        return <BankManagementTab />;
      case 'data':
        return <ProcessedDataTab />;
      case 'settings':
        return <SettingsTab />;
      default:
        return <UploadTab />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          {renderTabContent()}
        </div>
      </main>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Login />;
  }
  
  return children;
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="*" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthProvider>
  );
}

export default App;